import { Injectable, inject } from '@angular/core';
import {JWT_ACCESS_TOKEN, JWT_REFRESH_TOKEN} from '../../../utils/constants';
import { Router } from '@angular/router';
import { AuthApi, UserApi } from '../../../api/services';
import {firstValueFrom, Observable} from 'rxjs';
import { LoginResponseDto, UserRoleDto } from '../../../api/models';
import {first, tap} from 'rxjs/operators';
import { GoogleAuthService } from '../google-auth/google-auth.service';
import { PosthogService } from '../../../shared/services/posthog.service';

interface SetTokensPayload {
  accessToken: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly posthogService = inject(PosthogService);

  constructor(
    private userApi: UserApi,
    private authApi: AuthApi,
    private router: Router,
    private googleAuthService: GoogleAuthService) { }

  login(email: string, password: string): Observable<LoginResponseDto> {
    return this.userApi.userControllerLogin({ body: { email, password } }).pipe(
      tap(({ accessToken, refreshToken, uid }) => {
        this.setTokens({ accessToken, refreshToken });
        try {
          this.posthogService.client.identify(uid, { email });
        } catch (error) {
          console.error('Error identifying user in PostHog:', error);
        }
      })
    );
  }

  async loginWithGoogle(isSignUp = false, role: UserRoleDto = UserRoleDto.Customer): Promise<{ userId: string; isNew: boolean }> {
    const tokenResponse = await this.googleAuthService.init(isSignUp);
    const clientAccessToken = tokenResponse.access_token;
    const { accessToken, refreshToken, uid, isNew } = await firstValueFrom(
      this.userApi.userControllerLoginWithGoogle({
        body: { accessToken: clientAccessToken, isSignUp, role }
      })
    );
    this.setTokens({ accessToken, refreshToken });
    try {
      this.posthogService.client.identify(uid);
    } catch (error) {
      console.error('Error identifying user in PostHog:', error);
    }
    return { userId: uid, isNew };
  }

  async logout(): Promise<void> {
    if (this.isLoggedIn) {
      try {
        await firstValueFrom(this.authApi.authControllerLogOut());
      } catch {
        console.log('Logout Failed');
      }
    }

    this.setTokens({ accessToken: '', refreshToken: '' });
    this.posthogService.client.reset();
    if (this.router.url !== '/login') {
      window.location.reload();
    }
  }

  get isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  getAccessToken(): string {
    return localStorage.getItem(JWT_ACCESS_TOKEN);
  }

  getRefreshToken(): string {
    return localStorage.getItem(JWT_REFRESH_TOKEN);
  }

  setTokens({ accessToken, refreshToken }: SetTokensPayload): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  setAccessToken(value: string): void {
    localStorage.setItem(JWT_ACCESS_TOKEN, value);
  }

  setRefreshToken(value: string): void {
    localStorage.setItem(JWT_REFRESH_TOKEN, value);
  }

  refreshToken(): Observable<LoginResponseDto> {
    return this.authApi.authControllerRefreshToken().pipe(
      first(),
      tap(({ accessToken, refreshToken }) => {
        this.setTokens({ accessToken, refreshToken });
      }),
    );
  }

  getBeaconSignature() {
    return this.userApi.userControllerGetBeaconSignature()
  }

}
